import { ChartBarIcon } from '@heroicons/react/24/outline';
import { UsageGauge, USAGE_GAUGE_THRESHOLDS } from '../bot-usage/UsageGauge';
import { useMyBotUsage } from '../../hooks/useBotUsage';
import { formatUsageSourceSplit } from '../../services/api/botUsage';
import { formatDisplayDateTime } from '../../utils/format';
import { LEGACY_EN_NUMBER_LOCALE, formatNumber } from '../../utils/localeFormat';

/**
 * Phase 87 (Plan 87-08 / D-08) — "Claude usage" ProfilePage section, mounted
 * immediately after `MonkBotSection`. Copies `MonkBotSection.tsx`'s card
 * shell verbatim. Shows the user's own session/weekly gauges plus the
 * CodexBar-style Today / 30d cost / 30d tokens / last-report stat tiles the
 * user explicitly asked for (87-CONTEXT.md `<specifics>`).
 *
 * MKS-298 — the three cost/token tiles show the person's FULL Claude Code
 * usage from the org-wide OpenTelemetry export (their own sessions, their
 * Factory™ runs and Monka) instead of only the turns that went through Monka,
 * and a tile shows when their machine last reported. The two sources are never
 * added together: the export already contains the Monka turns.
 */

function formatUsd(value: number): string {
  return `$${value.toFixed(2)}`;
}

/** MKS-298 — nothing reported reads as a gap, never as `$0`. */
const NO_DATA = 'no data yet';

export function BotUsageGauges() {
  const { data: usage, isLoading } = useMyBotUsage();

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-3 mb-4">
        <ChartBarIcon className="h-6 w-6 text-gray-400" />
        <h2 className="text-lg font-semibold text-gray-900">Claude usage</h2>
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading...</p>
      ) : !usage ? (
        <p className="text-sm text-gray-500">No usage data yet.</p>
      ) : (
        <div className="space-y-4">
          <UsageGauge
            pct={usage.sessionPct}
            resetAt={usage.sessionResetAt}
            label="Session (5h)"
            ariaLabel="Your session usage"
          />
          <UsageGauge
            pct={usage.weeklyPct}
            resetAt={usage.weeklyResetAt}
            label="Weekly (7d)"
            ariaLabel="Your weekly usage"
          />

          {usage.weeklyPct !== null && usage.weeklyPct >= USAGE_GAUGE_THRESHOLDS.warn && (
            <div className="p-3 bg-amber-50 text-amber-800 rounded-lg text-sm">
              You&apos;re close to your weekly cap
              {usage.weeklyResetAt
                ? ` — it resets ${formatDisplayDateTime(usage.weeklyResetAt)}.`
                : '.'}
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500">Today</p>
              <p className="text-sm font-semibold text-gray-900">
                {usage.claudeCode ? formatUsd(usage.claudeCode.reportedCostUsdToday) : NO_DATA}
              </p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500">30d cost</p>
              <p className="text-sm font-semibold text-gray-900">
                {usage.claudeCode ? formatUsd(usage.claudeCode.reportedCostUsd30d) : NO_DATA}
              </p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500">30d tokens</p>
              <p className="text-sm font-semibold text-gray-900">
                {usage.claudeCode ? formatNumber(usage.claudeCode.tokens30d, undefined, LEGACY_EN_NUMBER_LOCALE) : NO_DATA}
              </p>
            </div>
            <div className="p-3 bg-gray-50 rounded-lg">
              <p className="text-xs text-gray-500">Last Claude Code report</p>
              <p className="text-sm font-semibold text-gray-900">
                {usage.claudeCode?.lastReportAt
                  ? formatDisplayDateTime(usage.claudeCode.lastReportAt)
                  : NO_DATA}
              </p>
            </div>
          </div>

          {usage.claudeCode?.bySource30d && (
            <p className="text-xs text-gray-600" data-testid="claude-usage-source-split">
              30d by source: {formatUsageSourceSplit(usage.claudeCode.bySource30d)}
            </p>
          )}

          <p className="text-xs text-gray-400">
            Cost and tokens are Anthropic&apos;s own figures for every Claude Code session run for
            you: your own machines, Monka and your Factory™ bot.
          </p>

          <p className="text-xs text-gray-400">
            Session and weekly gauges as of {formatDisplayDateTime(usage.lastCapturedAt)}
            {usage.lastSource ? ` via ${usage.lastSource}` : ''} — refreshed by your Monka turns,
            right after a Factory™ run, or a poll every 6 hours, so they lag behind work on your own
            machine.
          </p>
        </div>
      )}
    </div>
  );
}
